import userflow             from 'userflow.js';

import { Injectable }       from '@angular/core';

import { CompanyService }         from '../companies/company.service';
import { reportDatadogRumError }  from '../../app/datadog-rum.init';
import { SessionService }         from '../sessions/session.service';
import { UserflowGroupModel }     from './typings/userflow.group';
import { UserflowIdentityModel }  from './typings/userflow.identity';
import { UserflowSettingsModel }  from './typings/userflow.settings';

@Injectable()
export class UserflowService {
  private readonly _defaultLanguage    : string = 'en-us';
  private readonly _identifyTimeoutMs  : number = 30000;

  private readonly _servers : { [hostname: string]: string } = {
    'api.workshopsoftware.com.au'            : 'aus',
    'api.workshopsoftwareuk.com'             : 'eu',
    'api.workshopsoftware.com'               : 'us',
    'beta-api.workshopsoftware.com.au'       : 'beta',
    'beta-api.workshopsoftware.com'          : 'beta',
    'staging-api.workshopsoftware.com.au'    : 'staging',
    'development-api.workshopsoftware.com.au': 'dev',
    'api.local.workshopsoftware.com'         : 'local'
  };

  constructor(
    private companyService: CompanyService,
    private sessionService: SessionService
  ) {}

  createGroup () : UserflowGroupModel {
    const session = this.sessionService.base();
    const company = this.companyService.company;

    return {
      company_id         : company.id,
      country_code       : company.country_code,
      name               : company.company_name,
      signed_up_at       : session.company_signed_up_at,
      subscription_plan  : session.subscription_plan,
      subscription_status: company.subscription_status
    };
  }

  createIdentity ( settings: UserflowSettingsModel ) : UserflowIdentityModel {
    const session = this.sessionService.base();
    const company = this.companyService.company;

    return {
      company_id                      : company.id,
      company_name                    : company.company_name || company.name,
      country_code                    : company.country_code,
      dealer                          : company.dealer,
      demo_user                       : session.demo_user,
      email                           : session.username,
      first_login                     : company.first_login,
      has_active_sms_global_account   : company.has_active_sms_global_account,
      has_ashdown_ingram              : company.has_ashdown_ingram,
      has_autoinspect                 : company.has_autoinspect,
      has_carfax                      : company.has_carfax,
      has_carjam                      : company.has_carjam,
      has_checka                      : company.has_checka,
      has_customer_portal             : company.company_settings?.has_customer_portal,
      has_dealership                  : company.has_dealership,
      has_dvla_lookup                 : company.has_dvla_lookup,
      has_enform                      : company.has_enform,
      has_flippay                     : company.has_flippay,
      has_fulcrum                     : company.has_fulcrum,
      has_haynes_pro_lookup           : company.has_haynes_pro_lookup,
      has_hsy                         : company.hsy_settings?.has_hsy,
      has_mailchimp                   : company.has_mailchimp,
      has_myob                        : company.has_myob,
      has_partstech                   : company.partstech_settings?.has_partstech,
      has_prolink                     : company.has_prolink,
      has_quickbooks                  : company.has_quickbooks,
      has_registration_lookup         : company.registration_lookup_settings?.has_registration_lookup,
      has_repco                       : company.has_repco,
      has_sage                        : company.has_sage,
      has_stripe                      : company.stripe_settings?.has_stripe,
      has_supercheap                  : company.has_supercheap,
      has_tillpayments_payment_eft    : company.tillpayments_settings?.has_tillpayments_payment_eft,
      has_tillpayments_payment_gateway: company.tillpayments_settings?.has_tillpayments_payment_gateway,
      has_tiremetrix_registration     : company.tiremetrix_settings?.has_tiremetrix_tire_registration,
      has_tiremetrix_tpms             : company.tiremetrix_settings?.has_tiremetrix_tpms,
      has_tnp                         : company.tnp_settings?.has_tnp,
      has_vehicle_visuals             : company.has_vehicle_visuals,
      has_video_minutes               : company.has_video_minutes,
      has_workshop                    : company.has_workshop,
      has_xero                        : company.has_xero,
      haynes_pro_lookup_requests      : company.haynes_pro_lookup_requests,
      workshop_data_trial_started_at  : company.haynes_pro_trial_started_at,
      workshop_data_trial_ends_at     : company.haynes_pro_trial_ended_at,
      workshop_data_activated         : company.has_haynes_pro_lookup,
      workshop_data_trialing          : company.has_haynes_pro_trial,
      is_admin                        : session.admin,
      is_branch_owner                 : company.is_branch_owner,
      is_capricorn                    : company.is_capricorn,
      is_franchise_owner              : company.is_franchise_owner,
      is_multibranch                  : company.is_multibranch,
      is_region_owner                 : company.is_region_owner,
      limited_active                  : company.limited_active,
      name                            : session.full_name || session.username,
      needs_myob_setup                : company.needs_myob_setup,
      needs_quickbooks_setup          : company.needs_quickbooks_setup,
      needs_sage_one_aus_setup        : company.needs_sage_one_aus_setup,
      needs_sage_setup                : company.needs_sage_setup,
      needs_xero_setup                : company.needs_xero_setup,
      server                          : this.resolveServer(),
      signed_up_at                    : session.signed_up_at,
      subscription_plan               : session.subscription_plan,
      subscription_status             : session.subscription_status,
      user_id                         : settings.userId,
    };
  }

  getSettings () : UserflowSettingsModel | null {
    const company  = this.companyService.company;
    const session  = this.sessionService.base();

    const envKey                      = session?.product_onboarding?.env_key;
    const groupIdentityVerificationId = session?.product_onboarding?.group_identity_verification_id;
    const identityVerificationId      = session?.product_onboarding?.identity_verification_id;
    const userId                      = session?.user_id;

    return {
      defaultLanguage: this._defaultLanguage,
      envKey,
      groupId        : company?.id,
      groupIdentityVerificationId,
      identityVerificationId,
      userId         : userId
    };
  }

  /**
   * Initializes the SDK and identifies the current user.
   * Gets params from Company and Session services.
   */
  initialize () : void {
    if (!this.isSetup()) {
      console.error('User is not properly setup');

      return;
    }

    const settings = this.getSettings();
    const identity = this.createIdentity(settings);

    userflow.init(settings.envKey);

    const identified = userflow.identify(identity.user_id, identity, {
      signature: settings.identityVerificationId
    });

    this.watchIdentify(identified, identity);

    if (settings.groupId && settings.groupIdentityVerificationId) {
      userflow.group(settings.groupId, this.createGroup(), {
        signature: settings.groupIdentityVerificationId
      });
    }
  }

  isSetup () : boolean {
    const company  = this.companyService.company;
    const session  = this.sessionService.base();

    const envKey                 = session?.product_onboarding?.env_key;
    const identityVerificationId = session?.product_onboarding?.identity_verification_id;
    const userId                 = session?.user_id;

    if (!envKey || !identityVerificationId || !userId || !company?.id) {
      return false;
    }

    return true;
  }

  resolveServer () : string {
    const hostname = SERVER_ENDPOINT
      .replace(/^https?:\/\//, '')
      .split(/[/:]/)[0];

    return this._servers[hostname] || hostname;
  }

  private watchIdentify ( identified : Promise<void>, identity : UserflowIdentityModel ) : void {
    const context = {
      company_id  : identity.company_id,
      country_code: identity.country_code
    };

    const settled = identified.catch(() => 'rejected');

    const timeout = new Promise<string>(( resolve ) => {
      window.setTimeout(() => resolve('timeout'), this._identifyTimeoutMs);
    });

    Promise.race([ settled, timeout ])
    .then(( result ) => {
      if (result === 'timeout' && navigator.onLine && !userflow._stubbed) {
        reportDatadogRumError(new Error('Userflow identify did not complete'), context);
      }
    });
  }
}
